import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, ChevronDown, ChevronRight, Copy, Pin, Plus, Send, Trash2, X } from 'lucide-react';
import type { CRMUser } from '../../types';
import type { CashFlowProjection, CashFlowProjectionItem } from '../../utils/cashFlowProjection';
import {
  CAPITAL_PLAN_STATUSES,
  CAPITAL_PLAN_STATUS_LABELS,
  canTransitionCapitalPlanStatus,
  capitalPlanWhyToHtml,
  CAPEX_VIEWER_LEAD_ID,
  capexLineAttachmentPreviewKind,
  formatCapexOutOfCash,
  isCapitalPlanOnForecast,
  stackedCapexOverlayLines,
  type CapitalPlan,
  type CapitalPlanComment,
  type CapitalPlanStatus,
} from '../../utils/capitalPlans';
import CapexLineAttachments from './CapexLineAttachments';
import RichCommentEditor, {
  type RichCommentEditorHandle,
  type RichCommentEditorPayload,
} from '../pm/RichCommentEditor';
import RichCommentRenderer from '../pm/RichCommentRenderer';
import { isHtmlEmpty } from '../../utils/richComment';
import '../pm/TaskComments.css';
import {
  addCalendarDays,
  applyCapexBillToOutflow,
  applyCashFlowScenario,
  capexLinkedBill,
  capexOutflowLineAmount,
  coveredCapexBillIds,
  createBlankOutflow,
  createBlankProject,
  formatCapexBillOption,
  formatScenarioMoney,
  listShiftableItems,
  scenarioHasEffect,
  newScenarioId,
  projectCashDates,
  projectRolledTotal,
  projectionItemKey,
  sortCapexLinkableBills,
  supplierBillsById,
  syncCapexOutflowToLinkedBill,
  type ScenarioCustomSlice,
  type ScenarioLine,
  type ScenarioOutflow,
  type ScenarioOutflowAttachment,
  type ScenarioProject,
  type ScenarioSplit,
  type ScenarioTimingShift,
} from '../../utils/cashFlowScenario';
import {
  describeSupplierInstallmentTrigger,
  expandSupplierBillPayments,
  supplierBillPaidAmount,
  type SupplierBill,
} from '../../utils/supplierBills';

interface CashPlanningModalProps {
  baseline: CashFlowProjection;
  lines: ScenarioLine[];
  onChange: (lines: ScenarioLine[]) => void;
  onClose: () => void;
  title: string;
  why: string;
  onTitleChange: (value: string) => void;
  onWhyChange: (value: string) => void;
  plans: CapitalPlan[];
  activePlan: CapitalPlan | null;
  onOpenPlan: (planId: string) => void;
  onNewPlan: () => void;
  onDeletePlan?: (planId: string) => void;
  onShare: () => void;
  onSaveAs?: () => void;
  onStatus: (planId: string, status: CapitalPlanStatus) => void;
  comments: CapitalPlanComment[];
  onComment: (payload: { body: string; bodyHtml?: string; mentionUserIds?: string[] }) => void;
  canWrite?: boolean;
  canComment?: boolean;
  mentionableUsers: CRMUser[];
  canDecide: boolean;
  stackedPlanIds: string[];
  onToggleStacked: (planId: string) => void;
  busy?: boolean;
  error?: string | null;
  saveState?: 'idle' | 'saving' | 'saved';
  /** Live AP — used to link approved CapEx line items so they are not double-counted. */
  supplierBills?: SupplierBill[];
  projectNameById?: Map<string, string>;
  projectIdByDealId?: Map<string, string>;
  supplierTaskEndDates?: Map<string, Date>;
  taskNameById?: Map<string, string>;
  onEnsurePlanId?: () => Promise<string | null>;
}

const PdfViewerModal = lazy(() => import('../pdf/PdfViewerModal'));
const ImageViewerModal = lazy(() => import('../pdf/ImageViewerModal'));

const SPLIT_OPTIONS: Array<{ id: ScenarioSplit; label: string }> = [
  { id: 'lump', label: 'Lump sum' },
  { id: 'fifty_fifty', label: '50 / 50' },
  { id: 'deposit_net', label: 'Deposit + net' },
  { id: 'custom', label: 'Phases' },
];

type ComposerKind = 'purchase' | 'payment' | 'project';

const SHIFT_STEPS = [-30, -7, 7, 30];

function toInputDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromInputDate(s: string, fallback: Date): Date {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return fallback;
  return new Date(y, m - 1, d);
}

function monthLabel(key: string | null, projection: CashFlowProjection): string {
  if (!key) return '—';
  const m = projection.months.find((row) => row.monthKey === key);
  return m
    ? m.month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : key;
}

function runwayLabel(months: number | null, horizon: number): string {
  return months != null ? `${months} mo` : `${horizon}+ mo`;
}

function PlanRailRow({
  plan,
  selected,
  compared,
  canWrite,
  canDecide,
  busy,
  onOpen,
  onStatus,
  onToggleStacked,
  onDelete,
}: {
  plan: CapitalPlan;
  selected: boolean;
  compared: boolean;
  canWrite: boolean;
  canDecide: boolean;
  busy: boolean;
  onOpen: () => void;
  onStatus: (status: CapitalPlanStatus) => void;
  onToggleStacked: () => void;
  onDelete?: () => void;
}) {
  const baked = isCapitalPlanOnForecast(plan.status);
  const completed = plan.status === 'completed';
  const statusOptions = CAPITAL_PLAN_STATUSES.filter((status) =>
    canTransitionCapitalPlanStatus(plan.status, status, { canDecide }),
  );
  const showStatusSelect = (canWrite || canDecide) && statusOptions.length > 1;
  const statusLabel = baked
    ? 'On cash line'
    : CAPITAL_PLAN_STATUS_LABELS[plan.status];
  return (
    <div
      className={`fin-plan-row-wrap${selected ? ' is-active' : ''}${baked ? ' is-baked' : ''}${compared ? ' is-compared' : ''}${completed ? ' is-completed' : ''}`}
    >
      <div className="fin-plan-row-top">
        <button
          type="button"
          className="fin-plan-row"
          onClick={onOpen}
          aria-current={selected ? 'true' : undefined}
        >
          <span className="fin-plan-row-title">{plan.title}</span>
        </button>
        <div className="fin-plan-row-actions">
          {!baked && !completed && (
            <button
              type="button"
              className={`fin-plan-compare${compared || selected ? ' is-on' : ''}`}
              aria-pressed={compared || selected}
              aria-label={compared || selected ? `Stop comparing ${plan.title}` : `Compare ${plan.title} on the cash line`}
              disabled={selected}
              onClick={(e) => {
                e.stopPropagation();
                onToggleStacked();
              }}
            >
              <Pin size={12} aria-hidden="true" />
            </button>
          )}
          {canWrite && onDelete && (
            <button
              type="button"
              className="fin-plan-delete"
              aria-label={`Delete ${plan.title}`}
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 size={12} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      {showStatusSelect ? (
        <select
          className="fin-plan-status"
          value={plan.status}
          disabled={busy}
          aria-label={`Status of ${plan.title}`}
          onChange={(e) => onStatus(e.target.value as CapitalPlanStatus)}
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status === 'approved' && baked ? 'On cash line' : CAPITAL_PLAN_STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      ) : (
        <span className="fin-plan-row-meta">{statusLabel}</span>
      )}
    </div>
  );
}

function splitLabel(split: ScenarioSplit): string {
  return SPLIT_OPTIONS.find((opt) => opt.id === split)?.label ?? 'Lump sum';
}

function lineKind(line: ScenarioLine): string {
  if (line.type === 'project') return 'Project';
  if (line.type === 'shift') return 'Shift';
  return line.type === 'payment' ? 'Payment' : 'Purchase';
}

function lineName(line: ScenarioLine): string {
  if (line.type === 'shift') return 'Live installment';
  return line.label.trim() || 'Untitled';
}

function planCostTotal(lines: ScenarioLine[], billsById?: Map<string, SupplierBill>): number {
  return lines.reduce((sum, line) => {
    if (!line.enabled) return sum;
    if (line.type === 'project') return sum + projectRolledTotal(line, billsById);
    if (line.type === 'purchase' || line.type === 'payment') {
      return sum + capexOutflowLineAmount(line, capexLinkedBill(line, billsById));
    }
    return sum;
  }, 0);
}

function linkedItemCount(line: ScenarioLine): number {
  if (line.type === 'project') {
    return line.items.filter((item) => item.linkedBillId?.trim()).length;
  }
  if ((line.type === 'purchase' || line.type === 'payment') && line.linkedBillId?.trim()) return 1;
  return 0;
}

function lineMeta(
  line: ScenarioLine,
  billsById?: Map<string, SupplierBill>,
  taskEndDateById?: Map<string, Date>,
): string {
  if (line.type === 'project') {
    const total = projectRolledTotal(line, billsById);
    const span = projectCashDates(line, { billsById, taskEndDateById });
    const when = span.first && span.last
      ? span.first.getTime() === span.last.getTime()
        ? toInputDate(span.first)
        : `${toInputDate(span.first)} → ${toInputDate(span.last)}`
      : 'No dates';
    const linked = linkedItemCount(line);
    const files = lineAttachmentCount(line);
    const extras = [
      linked ? `${linked} linked` : '',
      files ? `${files} file${files === 1 ? '' : 's'}` : '',
    ].filter(Boolean);
    return extras.length
      ? `${formatScenarioMoney(total)} · ${when} · ${extras.join(' · ')}`
      : `${formatScenarioMoney(total)} · ${when}`;
  }
  if (line.type === 'shift') {
    return `${line.shiftDays > 0 ? '+' : ''}${line.shiftDays}d`;
  }
  const remaining = capexOutflowLineAmount(line, capexLinkedBill(line, billsById));
  const paidDropped = remaining < Math.round(Number(line.amount) || 0);
  const files = lineAttachmentCount(line);
  const fileNote = files ? ` · ${files} file${files === 1 ? '' : 's'}` : '';
  if (line.linkedBillId?.trim()) {
    return paidDropped
      ? `${formatScenarioMoney(remaining)} · tracks bill terms · paid dropped${fileNote}`
      : `${formatScenarioMoney(remaining)} · tracks bill terms${fileNote}`;
  }
  return `${formatScenarioMoney(remaining)} · ${toInputDate(line.date)} · ${splitLabel(line.split)}${fileNote}`;
}

function lineAttachmentCount(line: ScenarioLine): number {
  if (line.type === 'project') {
    return line.items.reduce((sum, item) => sum + (item.attachments?.length || 0), 0);
  }
  if (line.type === 'purchase' || line.type === 'payment') {
    return line.attachments?.length || 0;
  }
  return 0;
}

function itemSummary(item: CashFlowProjectionItem): string {
  const due = item.dueDate
    ? item.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : 'no date';
  return `${item.kind === 'ar' ? 'AR' : 'AP'} · ${item.projectLabel} · ${item.label} · ${formatScenarioMoney(item.amount)} · ${due}`;
}

const formatAxis = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? '−' : ''}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `${n < 0 ? '−' : ''}$${Math.round(abs / 1000)}k`;
  return formatScenarioMoney(n);
};

const CashPlanningModal: React.FC<CashPlanningModalProps> = ({
  baseline,
  lines,
  onChange,
  onClose,
  title,
  why,
  onTitleChange,
  onWhyChange,
  plans,
  activePlan,
  onOpenPlan,
  onNewPlan,
  onDeletePlan,
  onShare,
  onSaveAs,
  onStatus,
  comments,
  onComment,
  canWrite = true,
  canComment = true,
  mentionableUsers,
  canDecide,
  stackedPlanIds,
  onToggleStacked,
  busy = false,
  error = null,
  saveState = 'idle',
  supplierBills = [],
  projectNameById,
  projectIdByDealId,
  supplierTaskEndDates,
  taskNameById,
  onEnsurePlanId,
}) => {
  const [draft, setDraft] = useState<ScenarioOutflow>(() => createBlankOutflow('purchase'));
  const [composerKind, setComposerKind] = useState<ComposerKind>('purchase');
  const [openLineId, setOpenLineId] = useState<string | null>(null);
  const commentEditorRef = useRef<RichCommentEditorHandle>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [commentEmpty, setCommentEmpty] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CapitalPlan | null>(null);
  const [attachmentViewer, setAttachmentViewer] = useState<{
    kind: 'pdf' | 'image';
    url: string;
    title: string;
    documentId: string;
    mimeType?: string;
  } | null>(null);
  const [costsOpen, setCostsOpen] = useState(lines.length === 0);
  const [completedOpen, setCompletedOpen] = useState(false);

  useEffect(() => {
    setEditingTitle(false);
    setOpenLineId(null);
    setCostsOpen(!activePlan || activePlan.lines.length === 0);
    setComposerKind('purchase');
    setDraft(createBlankOutflow('purchase'));
  }, [activePlan?.id]);

  useEffect(() => {
    if (editingTitle) titleInputRef.current?.focus();
  }, [editingTitle]);
  const overlayLines = useMemo(
    () => stackedCapexOverlayLines({
      stackedPlanIds,
      plans,
      activePlanId: activePlan?.id,
      activeLines: lines,
    }),
    [activePlan?.id, lines, plans, stackedPlanIds],
  );
  const onCashLine = !!activePlan && isCapitalPlanOnForecast(activePlan.status);
  const openPlans = useMemo(() => plans.filter((p) => p.status !== 'completed'), [plans]);
  const completedPlans = useMemo(() => plans.filter((p) => p.status === 'completed'), [plans]);

  useEffect(() => {
    if (activePlan?.status === 'completed') setCompletedOpen(true);
  }, [activePlan?.id, activePlan?.status]);
  const extraOverlay = scenarioHasEffect(overlayLines);

  const previewCapexFile = (href: string, file: ScenarioOutflowAttachment) => {
    const kind = capexLineAttachmentPreviewKind(file);
    if (!kind) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    setAttachmentViewer({
      kind,
      url: href,
      title: file.name,
      documentId: `capex:${activePlan?.id || 'draft'}:${file.id || file.storagePath}`,
      mimeType: file.contentType,
    });
  };

  const coveredBillIds = useMemo(() => coveredCapexBillIds(supplierBills), [supplierBills]);
  const billsById = useMemo(() => supplierBillsById(supplierBills), [supplierBills]);

  useEffect(() => {
    if (!canWrite || !onCashLine || billsById.size === 0) return;
    let changed = false;
    const next = lines.map((line) => {
      if (line.type === 'project') {
        const items = line.items.map((item) => syncCapexOutflowToLinkedBill(
          item,
          capexLinkedBill(item, billsById),
          supplierTaskEndDates,
        ));
        const itemChanged = items.some((item, idx) => item !== line.items[idx]);
        if (!itemChanged) return line;
        changed = true;
        return { ...line, items };
      }
      if (line.type === 'purchase' || line.type === 'payment') {
        const synced = syncCapexOutflowToLinkedBill(
          line,
          capexLinkedBill(line, billsById),
          supplierTaskEndDates,
        );
        if (synced !== line) changed = true;
        return synced;
      }
      return line;
    });
    if (changed) onChange(next);
  }, [billsById, canWrite, lines, onCashLine, onChange, supplierTaskEndDates]);

  const applied = useMemo(
    () => applyCashFlowScenario(baseline, overlayLines, {
      coveredBillIds,
      bills: supplierBills,
      taskEndDateById: supplierTaskEndDates,
    }),
    [baseline, coveredBillIds, overlayLines, supplierBills, supplierTaskEndDates],
  );
  const billProjectName = (bill: SupplierBill): string | undefined => {
    const projectId = bill.projectId || (bill.dealId ? projectIdByDealId?.get(bill.dealId) : undefined);
    return projectId ? projectNameById?.get(projectId) : undefined;
  };
  const horizon = baseline.months.length;
  const shiftable = useMemo(() => listShiftableItems(baseline), [baseline]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (attachmentViewer) {
        setAttachmentViewer(null);
        return;
      }
      if (pendingDelete) {
        setPendingDelete(null);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [attachmentViewer, onClose, pendingDelete]);

  const chartRows = useMemo(() => {
    return baseline.months.map((m, i) => {
      const scenario = applied.projection.months[i];
      return {
        label: m.month.toLocaleDateString('en-US', { month: 'short' }),
        x: i,
        'Live cash': i === 0 ? baseline.startingBalance : m.endingBalance,
        'With this plan': scenario
          ? (i === 0 ? applied.projection.startingBalance : scenario.endingBalance)
          : null,
      };
    });
  }, [baseline, applied]);

  const patch = (id: string, next: Partial<ScenarioLine>) => {
    onChange(lines.map((line) => (line.id === id ? ({ ...line, ...next } as ScenarioLine) : line)));
  };

  const primaryOutflow = useMemo(() => {
    const outflows = lines.filter((line): line is ScenarioOutflow => (
      line.type === 'purchase' || line.type === 'payment'
    ));
    const hasProject = lines.some((line) => line.type === 'project');
    if (hasProject || outflows.length !== 1) return null;
    return outflows[0];
  }, [lines]);

  useEffect(() => {
    if (!primaryOutflow) return;
    setComposerKind(primaryOutflow.type === 'payment' ? 'payment' : 'purchase');
    setDraft(primaryOutflow);
  }, [primaryOutflow?.id]);

  const applyComposer = (next: Partial<ScenarioOutflow>) => {
    const merged: ScenarioOutflow = { ...draft, ...next };
    setDraft(merged);
    if (composerKind === 'project') return;
    if (primaryOutflow) {
      onChange(lines.map((line) => (line.id === primaryOutflow.id ? merged : line)));
      return;
    }
    if (lines.length === 0) {
      const created: ScenarioOutflow = {
        ...merged,
        id: merged.id || newScenarioId(merged.type === 'purchase' ? 'buy' : 'pay'),
      };
      setDraft(created);
      onChange([created]);
    }
  };

  const addDraft = () => {
    if (composerKind === 'project') {
      const project = createBlankProject(draft.date);
      project.label = draft.label.trim() || 'Shop expansion';
      onChange([...lines, project]);
      setDraft(createBlankOutflow('purchase', draft.date));
      return;
    }
    if (!(Number(draft.amount) > 0)) return;
    onChange([...lines, { ...draft, id: newScenarioId(draft.type === 'purchase' ? 'buy' : 'pay') }]);
    setDraft(createBlankOutflow(draft.type, draft.date));
  };

  const duplicate = (line: ScenarioLine) => {
    if (line.type === 'shift') {
      onChange([...lines, { ...line, id: newScenarioId('shift') }]);
      return;
    }
    if (line.type === 'project') {
      const copy: ScenarioProject = {
        ...line,
        id: newScenarioId('proj'),
        items: line.items.map((item) => ({ ...item, id: newScenarioId(item.type === 'purchase' ? 'buy' : 'pay') })),
      };
      onChange([...lines, copy]);
      return;
    }
    onChange([...lines, { ...line, id: newScenarioId(line.type === 'purchase' ? 'buy' : 'pay') }]);
  };

  const patchProject = (projectId: string, next: Partial<ScenarioProject>) => {
    onChange(lines.map((line) => (line.id === projectId && line.type === 'project' ? { ...line, ...next } : line)));
  };

  const patchProjectItem = (projectId: string, itemId: string, next: Partial<ScenarioOutflow>) => {
    onChange(lines.map((line) => {
      if (line.id !== projectId || line.type !== 'project') return line;
      return {
        ...line,
        items: line.items.map((item) => (item.id === itemId ? { ...item, ...next } : item)),
      };
    }));
  };

  const submitComment = (payload?: RichCommentEditorPayload) => {
    const next = payload ?? commentEditorRef.current?.getPayload();
    if (!next || next.isEmpty) return;
    onComment({
      body: next.bodyPlain,
      bodyHtml: next.bodyHtml,
      mentionUserIds: next.mentions.map((m) => m.userId),
    });
    commentEditorRef.current?.clear();
    setCommentEmpty(true);
  };

  const { safety } = applied;
  const liveFirstRed = baseline.months.find((m) => m.endingBalance < 0);
  const outOfCash = formatCapexOutOfCash({
    scenarioFirstRedLabel: safety.firstRedMonthKey
      ? monthLabel(safety.firstRedMonthKey, applied.projection)
      : null,
    liveFirstRedLabel: liveFirstRed
      ? monthLabel(liveFirstRed.monthKey, baseline)
      : null,
  });
  const compareCount = stackedPlanIds.filter((id) => {
    if (id === activePlan?.id) return false;
    const plan = plans.find((p) => p.id === id);
    return plan != null && plan.status !== 'approved' && plan.status !== 'completed';
  }).length;
  const planDisplayTitle = (title.trim() || activePlan?.title || 'New plan').trim();
  const overlayLegend = compareCount > 0
    ? `${planDisplayTitle} + ${compareCount} compared`
    : planDisplayTitle;

  return (
    <>
    <div className="fin-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="fin-modal fin-modal--planning"
        role="dialog"
        aria-labelledby="cash-planning-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fin-planning-body">
          <aside className="fin-planning-rail" aria-label="Capital plans">
            {canWrite && (
              <button
                type="button"
                className={`fin-plan-row fin-plan-row--new${!activePlan ? ' is-active' : ''}`}
                onClick={onNewPlan}
              >
                <Plus size={13} aria-hidden="true" />
                New plan
              </button>
            )}
            <nav className="fin-plan-list" aria-label="Saved plans">
              {plans.length === 0 && (
                <p className="fin-planning-empty">No saved plans yet.</p>
              )}
              {plans.length > 0 && openPlans.length === 0 && (
                <p className="fin-planning-empty">No open plans.</p>
              )}
              {openPlans.map((p) => (
                <PlanRailRow
                  key={p.id}
                  plan={p}
                  selected={activePlan?.id === p.id}
                  compared={!isCapitalPlanOnForecast(p.status) && activePlan?.id !== p.id && stackedPlanIds.includes(p.id)}
                  canWrite={canWrite}
                  canDecide={canDecide}
                  busy={busy}
                  onOpen={() => onOpenPlan(p.id)}
                  onStatus={(status) => onStatus(p.id, status)}
                  onToggleStacked={() => onToggleStacked(p.id)}
                  onDelete={onDeletePlan ? () => setPendingDelete(p) : undefined}
                />
              ))}
              {completedPlans.length > 0 && (
                <div className="fin-plan-completed">
                  <button
                    type="button"
                    className="fin-plan-completed-toggle"
                    aria-expanded={completedOpen}
                    onClick={() => setCompletedOpen((open) => !open)}
                  >
                    {completedOpen ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />}
                    Completed
                    <span className="fin-plan-completed-count">{completedPlans.length}</span>
                  </button>
                  {completedOpen && (
                    <div className="fin-plan-completed-list">
                      {completedPlans.map((p) => (
                        <PlanRailRow
                          key={p.id}
                          plan={p}
                          selected={activePlan?.id === p.id}
                          compared={false}
                          canWrite={canWrite}
                          canDecide={canDecide}
                          busy={busy}
                          onOpen={() => onOpenPlan(p.id)}
                          onStatus={(status) => onStatus(p.id, status)}
                          onToggleStacked={() => onToggleStacked(p.id)}
                          onDelete={onDeletePlan ? () => setPendingDelete(p) : undefined}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>
          </aside>

          <section className="fin-planning-stage">
            <header className="fin-planning-stage-head">
              <div className="fin-planning-stage-title">
                {canWrite && editingTitle ? (
                  <input
                    ref={titleInputRef}
                    id="cash-planning-title"
                    className="fin-planning-title-input"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    placeholder="Untitled plan"
                    onBlur={() => setEditingTitle(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') {
                        e.preventDefault();
                        setEditingTitle(false);
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    id="cash-planning-title"
                    className="fin-planning-title-text"
                    onClick={() => {
                      if (canWrite) setEditingTitle(true);
                    }}
                    disabled={!canWrite}
                  >
                    {planDisplayTitle}
                  </button>
                )}
                <p className={`fin-planning-kicker${onCashLine ? ' is-baked' : ''}`}>
                  {activePlan
                    ? activePlan.status === 'completed'
                      ? 'Completed · off the projected cash line'
                      : activePlan.status === 'approved'
                        ? 'Approved · permanently on the projected cash line'
                        : `${CAPITAL_PLAN_STATUS_LABELS[activePlan.status]} · discussing this plan`
                    : 'Unsaved plan — pick a title to save, then discuss it here.'}
                </p>
              </div>
              <div className="fin-planning-stage-actions">
                {canWrite && (!activePlan || activePlan.status === 'draft' || activePlan.status === 'needs_info') && (
                  <button
                    type="button"
                    className="fin-primary-btn"
                    onClick={onShare}
                    disabled={busy}
                  >
                    Share
                  </button>
                )}
                <p
                  className={`fin-planning-saved${saveState === 'saving' ? ' is-saving' : ''}`}
                  role="status"
                >
                  {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Saves as you edit'}
                </p>
                <button type="button" className="fin-modal-close" onClick={onClose} aria-label="Close cash plan">
                  <X size={16} />
                </button>
              </div>
            </header>

            {error && <div className="fin-modal-error">{error}</div>}

            <div className="fin-planning-chart-row">
              <div className="fin-planning-chart">
                <div className="fin-planning-legend" aria-hidden="true">
                  <span className="fin-planning-legend-item">
                    <span className="fin-planning-legend-line is-live" />
                    Projected cash
                  </span>
                  {extraOverlay && (
                    <span className="fin-planning-legend-item">
                      <span className="fin-planning-legend-line is-plan" />
                      {overlayLegend}
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartRows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} tickFormatter={formatAxis} width={56} />
                    <Tooltip
                      formatter={(value) => formatScenarioMoney(typeof value === 'number' ? value : Number(value) || 0)}
                      contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, color: 'var(--color-text-primary)' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Live cash"
                      name="Projected cash"
                      stroke={onCashLine && !extraOverlay ? '#0369A1' : '#94A3B8'}
                      strokeWidth={onCashLine && !extraOverlay ? 2.4 : 1.8}
                      strokeDasharray={onCashLine && !extraOverlay ? undefined : '5 4'}
                      dot={false}
                      isAnimationActive={false}
                    />
                    {extraOverlay && (
                      <Line
                        type="monotone"
                        dataKey="With this plan"
                        name={overlayLegend}
                        stroke="#0369A1"
                        strokeWidth={2.4}
                        dot={false}
                        isAnimationActive={false}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="fin-planning-safety">
                <div className="fin-planning-safety-card">
                  <span className="fin-kpi-label">Out of cash</span>
                  <span className={`fin-kpi-value ${outOfCash.ranOut ? 'fin-planning-danger' : 'fin-planning-ok'}`}>
                    {outOfCash.value}
                  </span>
                  <span className="fin-kpi-sub">{outOfCash.sub}</span>
                </div>
                <div className="fin-planning-safety-card">
                  <span className="fin-kpi-label">Runway</span>
                  <span className={`fin-kpi-value ${safety.scenarioRunway != null ? 'fin-planning-danger' : 'fin-planning-ok'}`}>
                    {runwayLabel(safety.scenarioRunway, horizon)}
                  </span>
                  <span className="fin-kpi-sub">
                    Live {runwayLabel(safety.baselineRunway, horizon)}
                  </span>
                </div>
              </div>
            </div>

            <div className={`fin-planning-work${costsOpen ? '' : ' is-collapsed'}`}>
            <button
              type="button"
              className="fin-planning-work-toggle"
              aria-expanded={costsOpen}
              onClick={() => setCostsOpen((open) => !open)}
            >
              <ChevronRight size={14} className={costsOpen ? 'is-open' : ''} aria-hidden="true" />
              <span className="fin-planning-work-toggle-title">Costs</span>
              <span className="fin-planning-work-toggle-meta">
                {lines.length === 0
                  ? 'None yet'
                  : `${formatScenarioMoney(planCostTotal(lines, billsById))} · ${lines.length} ${lines.length === 1 ? 'item' : 'items'}`}
              </span>
            </button>
            {costsOpen && (
            <>
            {canWrite && (
            <div className="fin-planning-composer">
              <div className="fin-planning-composer-row">
                <div className="fin-forecast-toggle" role="group" aria-label="Line type">
                  <button
                    type="button"
                    className={composerKind === 'purchase' ? 'active' : ''}
                    onClick={() => {
                      setComposerKind('purchase');
                      applyComposer({
                        type: 'purchase',
                        label: draft.label === 'Extra payment' || draft.label === 'Shop expansion' ? 'Planned purchase' : draft.label,
                      });
                    }}
                  >
                    Purchase
                  </button>
                  <button
                    type="button"
                    className={composerKind === 'payment' ? 'active' : ''}
                    onClick={() => {
                      setComposerKind('payment');
                      applyComposer({
                        type: 'payment',
                        label: draft.label === 'Planned purchase' || draft.label === 'Shop expansion' ? 'Extra payment' : draft.label,
                      });
                    }}
                  >
                    Extra payment
                  </button>
                  <button
                    type="button"
                    className={composerKind === 'project' ? 'active' : ''}
                    onClick={() => {
                      setComposerKind('project');
                      setDraft((d) => ({
                        ...d,
                        type: 'purchase',
                        label: d.label === 'Planned purchase' || d.label === 'Extra payment' ? 'Shop expansion' : d.label,
                      }));
                    }}
                  >
                    Project
                  </button>
                </div>
                <label className="fin-modal-field">
                  <span className="fin-modal-label">{composerKind === 'project' ? 'Project' : 'Label'}</span>
                  <input
                    className="fin-modal-input"
                    value={draft.label}
                    onChange={(e) => applyComposer({ label: e.target.value })}
                    placeholder={composerKind === 'project' ? 'Shop expansion…' : 'Planned purchase'}
                  />
                </label>
                {composerKind !== 'project' && (
                  <>
                    <label className="fin-modal-field fin-modal-field--narrow">
                      <span className="fin-modal-label">Amount</span>
                      <input
                        className="fin-modal-input"
                        type="number"
                        min={0}
                        step={100}
                        value={draft.amount || ''}
                        onChange={(e) => applyComposer({ amount: Number(e.target.value) || 0 })}
                      />
                    </label>
                    <label className="fin-modal-field fin-modal-field--narrow">
                      <span className="fin-modal-label">{draft.split === 'lump' ? 'Date' : 'First pay'}</span>
                      <input
                        className="fin-modal-input"
                        type="date"
                        value={toInputDate(draft.date)}
                        onChange={(e) => applyComposer({ date: fromInputDate(e.target.value, draft.date) })}
                      />
                    </label>
                  </>
                )}
                {(!primaryOutflow || composerKind === 'project') && (
                  <button
                    type="button"
                    className="fin-primary-btn"
                    onClick={addDraft}
                    disabled={composerKind !== 'project' && !(Number(draft.amount) > 0)}
                  >
                    <Plus size={13} /> Add
                  </button>
                )}
              </div>
            </div>
            )}

            <div className="fin-planning-lines">
              {lines.length > 0 && (
                <div className="fin-planning-lines-head">
                  <span className="fin-modal-label">On this plan</span>
                  <button
                    type="button"
                    className="fin-secondary-btn"
                    onClick={() => onChange([])}
                    disabled={!canWrite}
                  >
                    Clear
                  </button>
                </div>
              )}
              {lines.map((line) => {
                const open = openLineId === line.id;
                return (
                  <article
                    key={line.id}
                    className={`fin-plan-item${open ? ' is-open' : ''}${line.enabled ? '' : ' is-off'}`}
                  >
                    <button
                      type="button"
                      className="fin-plan-item-main"
                      aria-expanded={open}
                      onClick={() => setOpenLineId(open ? null : line.id)}
                    >
                      <ChevronRight size={14} className={open ? 'is-open' : ''} aria-hidden="true" />
                      <span className="fin-plan-item-kind">{lineKind(line)}</span>
                      <span className="fin-plan-item-name">{lineName(line)}</span>
                      <span className="fin-plan-item-meta">{lineMeta(line, billsById, supplierTaskEndDates)}</span>
                    </button>
                    <div className="fin-planning-line-actions">
                      <button type="button" className="fin-icon-quiet" aria-label="Duplicate" onClick={() => duplicate(line)}>
                        <Copy size={13} />
                      </button>
                      <button
                        type="button"
                        className="fin-icon-quiet"
                        aria-label="Remove"
                        onClick={() => {
                          if (openLineId === line.id) setOpenLineId(null);
                          onChange(lines.filter((l) => l.id !== line.id));
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    {open && (
                      <div className="fin-plan-item-editor">
                        {line.type === 'project' ? (
                          <ProjectCard
                            project={line}
                            open
                            bare
                            onToggleOpen={() => setOpenLineId(null)}
                            onPatch={(next) => patchProject(line.id, next)}
                            onPatchItem={(itemId, next) => patchProjectItem(line.id, itemId, next)}
                            onDuplicate={() => duplicate(line)}
                            onRemove={() => {
                              setOpenLineId(null);
                              onChange(lines.filter((l) => l.id !== line.id));
                            }}
                            allowBillLink={onCashLine}
                            supplierBills={supplierBills}
                            billProjectName={billProjectName}
                            canWrite={canWrite}
                            billsById={billsById}
                            taskEndDateById={supplierTaskEndDates}
                            taskNameById={taskNameById}
                            planId={activePlan?.id ?? null}
                            onEnsurePlanId={onEnsurePlanId}
                            onPreviewFile={previewCapexFile}
                          />
                        ) : line.type === 'shift' ? (
                          <ShiftRow
                            line={line}
                            items={shiftable}
                            onPatch={(next) => patch(line.id, next)}
                          />
                        ) : (
                          <OutflowRow
                            line={line}
                            onPatch={(next) => patch(line.id, next)}
                            allowBillLink={onCashLine}
                            supplierBills={supplierBills}
                            billProjectName={billProjectName}
                            canWrite={canWrite}
                            taskEndDateById={supplierTaskEndDates}
                            taskNameById={taskNameById}
                            planId={activePlan?.id ?? null}
                            onEnsurePlanId={onEnsurePlanId}
                            onPreviewFile={previewCapexFile}
                          />
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            </>
            )}
            </div>

            <div className="fin-planning-thread">
              <header className="fin-planning-thread-head">
                <h4 className="fin-planning-thread-title">Discussion · {planDisplayTitle}</h4>
                <span className="fin-planning-thread-count">
                  {comments.length === 0
                    ? 'No replies yet'
                    : `${comments.length} ${comments.length === 1 ? 'reply' : 'replies'}`}
                </span>
              </header>
              <ul className="fin-planning-comments">
                <li className="fin-planning-comment is-case">
                  <div className="fin-planning-comment-head">
                    <span className="fin-planning-why-label">The case</span>
                    <strong>{activePlan?.createdByName || 'You'}</strong>
                    {activePlan && (
                      <span className="fin-planning-comment-time">
                        {activePlan.createdAt.toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                  </div>
                  {canWrite ? (
                    <RichCommentEditor
                      key={activePlan?.id ?? 'new-plan'}
                      initialHtml={capitalPlanWhyToHtml(why)}
                      mentionableUsers={mentionableUsers}
                      taskId={`capex-why-${activePlan?.id || 'draft'}`}
                      onChange={onWhyChange}
                      placeholder="What capability does this add, and what do we do today instead?"
                      disabled={busy}
                    />
                  ) : (
                    <div className="fin-planning-why-read">
                      {isHtmlEmpty(capitalPlanWhyToHtml(why)) ? (
                        <p className="fin-planning-empty">No case written for this plan yet.</p>
                      ) : (
                        <RichCommentRenderer
                          bodyHtml={capitalPlanWhyToHtml(why)}
                          body={why}
                          enlarged
                        />
                      )}
                    </div>
                  )}
                </li>
                {comments.map((c) => (
                  <li key={c.id} className="fin-planning-comment">
                    <div className="fin-planning-comment-head">
                      <strong>{c.authorName}</strong>
                      <span className="fin-planning-comment-time">
                        {c.createdAt.toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <RichCommentRenderer bodyHtml={c.bodyHtml} body={c.body} enlarged />
                  </li>
                ))}
              </ul>

              {activePlan && canComment && (
                <div className="fin-planning-comment-compose">
                  <RichCommentEditor
                    ref={commentEditorRef}
                    mentionableUsers={mentionableUsers}
                    taskId={`capex-${activePlan.id}`}
                    onChange={(html) => setCommentEmpty(isHtmlEmpty(html))}
                    onSubmit={(payload) => submitComment(payload)}
                    placeholder={`Reply on “${planDisplayTitle}”… (@ to mention, ⌘/Ctrl-Enter to send)`}
                    disabled={busy}
                  />
                  <div className="fin-planning-comment-actions">
                    <button
                      type="button"
                      className="fin-primary-btn"
                      disabled={busy || commentEmpty}
                      onClick={() => submitComment()}
                    >
                      <Send size={13} /> Post
                    </button>
                  </div>
                </div>
              )}
              {!activePlan && (
                <p className="fin-planning-empty">Add a title or a line so this plan is saved, then you can reply.</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
    {pendingDelete && (
      <div
        className="fin-modal-overlay"
        onClick={() => {
          if (!busy) setPendingDelete(null);
        }}
        role="presentation"
      >
        <div
          className="fin-modal fin-modal--confirm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="capex-delete-title"
          aria-describedby="capex-delete-desc"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="fin-modal-header">
            <div className="fin-confirm-title">
              <AlertTriangle size={16} aria-hidden="true" />
              <h3 id="capex-delete-title">Delete this plan?</h3>
            </div>
            <button
              type="button"
              className="fin-modal-close"
              aria-label="Cancel delete"
              disabled={busy}
              onClick={() => setPendingDelete(null)}
            >
              <X size={16} />
            </button>
          </div>
          <div className="fin-modal-body">
            <p id="capex-delete-desc">
              Delete <strong>{pendingDelete.title}</strong>? This cannot be undone.
              {pendingDelete.status === 'approved' && (
                <> It is on the projected cash line and will come off Forecast.</>
              )}
            </p>
          </div>
          <div className="fin-modal-actions">
            <button
              type="button"
              className="fin-secondary-btn"
              disabled={busy}
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fin-danger-btn"
              disabled={busy}
              onClick={() => {
                const id = pendingDelete.id;
                setPendingDelete(null);
                onDeletePlan?.(id);
              }}
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      </div>
    )}
    {attachmentViewer && (
      <Suspense fallback={null}>
        {attachmentViewer.kind === 'pdf' ? (
          <PdfViewerModal
            key={attachmentViewer.documentId}
            url={attachmentViewer.url}
            title={attachmentViewer.title}
            documentId={attachmentViewer.documentId}
            leadId={CAPEX_VIEWER_LEAD_ID}
            leadName={planDisplayTitle}
            disableTakeoff
            onClose={() => setAttachmentViewer(null)}
          />
        ) : (
          <ImageViewerModal
            key={attachmentViewer.documentId}
            url={attachmentViewer.url}
            title={attachmentViewer.title}
            documentId={attachmentViewer.documentId}
            leadId={CAPEX_VIEWER_LEAD_ID}
            mimeType={attachmentViewer.mimeType}
            onClose={() => setAttachmentViewer(null)}
          />
        )}
      </Suspense>
    )}
    </>
  );
};

function capexBillDueLabel(
  slice: { dueDate: Date | null; estimated?: boolean },
  trigger: 'on_approval' | 'task_complete' | 'fixed_date' | undefined,
  taskTitle?: string,
): string {
  if (slice.dueDate) {
    const when = slice.dueDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (trigger === 'task_complete') return `due ${when} (tracks schedule)`;
    return `due ${when}`;
  }
  if (trigger === 'task_complete') {
    return `due when ${taskTitle || 'the task'} completes`;
  }
  return 'due date TBD';
}

function CapexLinkedBillTerms({
  bill,
  taskEndDateById,
  taskNameById,
}: {
  bill: SupplierBill;
  taskEndDateById?: Map<string, Date>;
  taskNameById?: Map<string, string>;
}) {
  const terms = bill.paymentTerms;
  const slices = expandSupplierBillPayments(bill, { taskEndDateById });
  if (!terms?.installments.length) {
    return (
      <p className="fin-capex-bill-link-hint">
        {bill.dueDate
          ? `Lump sum due ${bill.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`
          : 'This bill is a lump sum with no installment schedule.'}
      </p>
    );
  }
  return (
    <div className="fin-capex-bill-terms">
      <span className="fin-modal-label">Payment terms from bill</span>
      <ul>
        {slices.map((slice) => {
          const inst = slice.installmentIndex !== undefined
            ? terms.installments[slice.installmentIndex]
            : undefined;
          const taskTitle = inst?.taskTitle
            || (inst?.taskId ? taskNameById?.get(inst.taskId) : undefined);
          const titled = inst?.trigger === 'task_complete' && taskTitle
            ? { ...inst, taskTitle }
            : inst;
          const paid = slice.installmentIndex !== undefined
            && !!bill.installmentPayments?.[slice.installmentIndex];
          return (
            <li key={`${bill.id}-${slice.installmentIndex ?? slice.label}`} className={paid ? 'is-paid' : ''}>
              <strong>{formatScenarioMoney(slice.amount)}</strong>
              <span>
                {slice.label}
                {titled ? ` · ${describeSupplierInstallmentTrigger(titled)}` : ''}
              </span>
              <span>
                {capexBillDueLabel(slice, inst?.trigger, taskTitle)}
                {paid ? ' · paid' : ''}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CapexBillLinkField({
  line,
  bills,
  billProjectName,
  onPatch,
  canWrite,
  taskEndDateById,
}: {
  line: ScenarioOutflow;
  bills: SupplierBill[];
  billProjectName: (bill: SupplierBill) => string | undefined;
  onPatch: (next: Partial<ScenarioOutflow>) => void;
  canWrite: boolean;
  taskEndDateById?: Map<string, Date>;
}) {
  const options = sortCapexLinkableBills(bills, {
    vendorName: line.vendorName,
    amount: line.amount,
  });
  const linkedId = line.linkedBillId?.trim() || '';
  const linked = bills.find((bill) => bill.id === linkedId);
  const linkedInOptions = options.some((bill) => bill.id === linkedId);
  if (options.length === 0 && !linkedId) {
    return (
      <p className="fin-capex-bill-link-hint">No live bills to link yet — award or upload the payable first.</p>
    );
  }
  return (
    <label className="fin-modal-field full fin-capex-bill-link">
      <span className="fin-modal-label">Link existing bill</span>
      <select
        className="fin-modal-select"
        value={linkedId}
        disabled={!canWrite}
        onChange={(e) => {
          const nextId = e.target.value;
          if (!nextId) {
            onPatch({ linkedBillId: undefined });
            return;
          }
          const nextBill = bills.find((bill) => bill.id === nextId);
          onPatch(nextBill
            ? applyCapexBillToOutflow(nextBill, line, { taskEndDateById })
            : { linkedBillId: nextId });
        }}
      >
        <option value="">Not linked — count this CapEx amount</option>
        {linkedId && !linkedInOptions && (
          <option value={linkedId}>
            {linked
              ? `${formatCapexBillOption(linked, billProjectName(linked))} (not on cash line)`
              : 'Linked bill no longer available'}
          </option>
        )}
        {options.map((bill) => (
          <option key={bill.id} value={bill.id}>
            {formatCapexBillOption(bill, billProjectName(bill))}
          </option>
        ))}
      </select>
      <span className="fin-capex-bill-link-hint">
        {(() => {
          if (!linkedId) {
            return 'Use this when the spend is already an approved quote or payable so it is not counted twice.';
          }
          const remaining = capexOutflowLineAmount(line, linked);
          const paid = linked ? supplierBillPaidAmount(linked) : 0;
          if (paid > 0 && remaining <= 0) {
            return `${formatScenarioMoney(paid)} already paid on this bill is dropped from the line.`;
          }
          if (paid > 0) {
            return `${formatScenarioMoney(paid)} already paid is dropped from the line. ${formatScenarioMoney(remaining)} remaining, on this bill’s terms.`;
          }
          return 'Amount, vendor, and remaining unpaid installments come from this bill — including payments due when a task completes.';
        })()}
      </span>
    </label>
  );
}

function OutflowSplitFields({
  line,
  onPatch,
  locked = false,
}: {
  line: ScenarioOutflow;
  onPatch: (next: Partial<ScenarioOutflow>) => void;
  locked?: boolean;
}) {
  return (
    <>
      <div className="fin-forecast-toggle" role="group" aria-label="Split">
        {SPLIT_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={line.split === opt.id ? 'active' : ''}
            disabled={locked}
            onClick={() => onPatch({ split: opt.id })}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {(line.split === 'fifty_fifty' || line.split === 'deposit_net') && (
        <div className="fin-modal-grid">
          {line.split === 'deposit_net' && (
            <label className="fin-modal-field">
              <span className="fin-modal-label">Deposit %</span>
              <input
                className="fin-modal-input"
                type="number"
                min={1}
                max={99}
                value={line.depositPercent}
                disabled={locked}
                onChange={(e) => onPatch({ depositPercent: Number(e.target.value) || 50 })}
              />
            </label>
          )}
          <label className="fin-modal-field">
            <span className="fin-modal-label">Net days</span>
            <input
              className="fin-modal-input"
              type="number"
              min={0}
              value={line.netDays}
              disabled={locked}
              onChange={(e) => onPatch({ netDays: Number(e.target.value) || 0 })}
            />
          </label>
        </div>
      )}

      {line.split === 'custom' && (
        <div className="fin-planning-custom">
          {line.customSlices.map((slice, idx) => (
            <div key={slice.id} className="fin-planning-phase-row">
              <input
                className="fin-modal-input"
                placeholder="Phase"
                value={slice.label ?? ''}
                disabled={locked}
                onChange={(e) => onPatch({
                  customSlices: line.customSlices.map((s) => (s.id === slice.id ? { ...s, label: e.target.value } : s)),
                })}
              />
              <input
                className="fin-modal-input"
                type="number"
                min={0}
                placeholder="Amount"
                value={slice.amount ?? ''}
                disabled={locked}
                onChange={(e) => {
                  const amount = Number(e.target.value) || 0;
                  onPatch({
                    customSlices: line.customSlices.map((s) => (s.id === slice.id ? { ...s, amount } : s)),
                  });
                }}
              />
              <input
                className="fin-modal-input"
                type="date"
                value={toInputDate(slice.date)}
                disabled={locked}
                onChange={(e) => {
                  const date = fromInputDate(e.target.value, slice.date);
                  onPatch({
                    customSlices: line.customSlices.map((s) => (s.id === slice.id ? { ...s, date } : s)),
                  });
                }}
              />
              <button
                type="button"
                className="fin-icon-quiet"
                aria-label={`Remove phase ${idx + 1}`}
                disabled={locked}
                onClick={() => onPatch({
                  customSlices: line.customSlices.filter((s) => s.id !== slice.id),
                })}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="fin-secondary-btn"
            disabled={locked}
            onClick={() => {
              const next: ScenarioCustomSlice = {
                id: newScenarioId('slice'),
                amount: 0,
                date: addCalendarDays(line.date, line.customSlices.length * 30),
                label: `Phase ${line.customSlices.length + 1}`,
              };
              onPatch({ customSlices: [...line.customSlices, next] });
            }}
          >
            <Plus size={13} /> Add phase
          </button>
        </div>
      )}
    </>
  );
}

function ProjectCard({
  project,
  open,
  bare,
  onToggleOpen,
  onPatch,
  onPatchItem,
  onDuplicate,
  onRemove,
  allowBillLink = false,
  supplierBills = [],
  billProjectName,
  canWrite = true,
  billsById,
  taskEndDateById,
  taskNameById,
  planId = null,
  onEnsurePlanId,
  onPreviewFile,
}: {
  project: ScenarioProject;
  open: boolean;
  bare?: boolean;
  onToggleOpen: () => void;
  onPatch: (next: Partial<ScenarioProject>) => void;
  onPatchItem: (itemId: string, next: Partial<ScenarioOutflow>) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  allowBillLink?: boolean;
  supplierBills?: SupplierBill[];
  billProjectName?: (bill: SupplierBill) => string | undefined;
  canWrite?: boolean;
  billsById?: Map<string, SupplierBill>;
  taskEndDateById?: Map<string, Date>;
  taskNameById?: Map<string, string>;
  planId?: string | null;
  onEnsurePlanId?: () => Promise<string | null>;
  onPreviewFile?: (href: string, file: ScenarioOutflowAttachment) => void;
}) {
  const total = projectRolledTotal(project, billsById);
  const span = projectCashDates(project, { billsById, taskEndDateById });
  const spanLabel = span.first && span.last
    ? span.first.getTime() === span.last.getTime()
      ? toInputDate(span.first)
      : `${toInputDate(span.first)} → ${toInputDate(span.last)}`
    : 'No cash dates yet';

  return (
    <article className={bare ? 'fin-planning-project-bare' : `fin-planning-project ${project.enabled ? '' : 'is-off'}`}>
      {!bare && (
      <header className="fin-planning-project-head">
        <button
          type="button"
          className="fin-icon-quiet"
          aria-expanded={open}
          aria-label={open ? 'Collapse project' : 'Expand project'}
          onClick={onToggleOpen}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <label className="fin-modal-toggle">
          <input
            type="checkbox"
            checked={project.enabled}
            onChange={(e) => onPatch({ enabled: e.target.checked })}
          />
          <span>Project</span>
        </label>
        <input
          className="fin-modal-input fin-planning-project-title"
          value={project.label}
          onChange={(e) => onPatch({ label: e.target.value })}
          aria-label="Project name"
        />
        <span className="fin-planning-line-meta">
          {formatScenarioMoney(total)} · {spanLabel} · {project.items.length} item{project.items.length === 1 ? '' : 's'}
        </span>
        <div className="fin-planning-line-actions">
          <button type="button" className="fin-icon-quiet" aria-label="Duplicate project" onClick={onDuplicate}>
            <Copy size={13} />
          </button>
          <button type="button" className="fin-icon-quiet" aria-label="Remove project" onClick={onRemove}>
            <Trash2 size={13} />
          </button>
        </div>
      </header>
      )}
      {open && (
        <div className="fin-planning-project-items">
          {bare && (
            <label className="fin-modal-field">
              <span className="fin-modal-label">Project name</span>
              <input
                className="fin-modal-input"
                value={project.label}
                onChange={(e) => onPatch({ label: e.target.value })}
              />
            </label>
          )}
          {project.items.map((item) => (
            <div key={item.id} className={`fin-planning-project-item ${item.enabled ? '' : 'is-off'}`}>
              <ProjectItemEditor
                line={item}
                onPatch={(next) => onPatchItem(item.id, next)}
                onRemove={() => onPatch({ items: project.items.filter((i) => i.id !== item.id) })}
                allowBillLink={allowBillLink}
                supplierBills={supplierBills}
                billProjectName={billProjectName}
                canWrite={canWrite}
                taskEndDateById={taskEndDateById}
                taskNameById={taskNameById}
                planId={planId}
                onEnsurePlanId={onEnsurePlanId}
                onPreviewFile={onPreviewFile}
              />
            </div>
          ))}
          <button
            type="button"
            className="fin-secondary-btn"
            onClick={() => onPatch({ items: [...project.items, createBlankOutflow('purchase', project.items[0]?.date)] })}
          >
            <Plus size={13} /> Add line item
          </button>
        </div>
      )}
    </article>
  );
}

function linkedCapexDisplayAmount(line: ScenarioOutflow, bills: SupplierBill[]): number {
  const linked = bills.find((bill) => bill.id === line.linkedBillId?.trim());
  return capexOutflowLineAmount(line, linked);
}

function ProjectItemEditor({
  line,
  onPatch,
  onRemove,
  allowBillLink = false,
  supplierBills = [],
  billProjectName,
  canWrite = true,
  taskEndDateById,
  taskNameById,
  planId = null,
  onEnsurePlanId,
  onPreviewFile,
}: {
  line: ScenarioOutflow;
  onPatch: (next: Partial<ScenarioOutflow>) => void;
  onRemove: () => void;
  allowBillLink?: boolean;
  supplierBills?: SupplierBill[];
  billProjectName?: (bill: SupplierBill) => string | undefined;
  canWrite?: boolean;
  taskEndDateById?: Map<string, Date>;
  taskNameById?: Map<string, string>;
  planId?: string | null;
  onEnsurePlanId?: () => Promise<string | null>;
  onPreviewFile?: (href: string, file: ScenarioOutflowAttachment) => void;
}) {
  const lockedToBill = !!line.linkedBillId?.trim();
  const linkedBill = supplierBills.find((bill) => bill.id === line.linkedBillId?.trim());
  return (
    <div className="fin-planning-line-main">
      <div className="fin-planning-project-item-head">
        <input
          className="fin-modal-input fin-planning-project-title"
          value={line.label}
          onChange={(e) => onPatch({ label: e.target.value })}
          aria-label="Line item"
        />
        <button type="button" className="fin-icon-quiet" aria-label="Remove line item" onClick={onRemove}>
          <Trash2 size={13} />
        </button>
      </div>
      <div className="fin-modal-grid">
        <label className="fin-modal-field">
          <span className="fin-modal-label">Vendor</span>
          <input
            className="fin-modal-input"
            value={line.vendorName}
            disabled={lockedToBill}
            onChange={(e) => onPatch({ vendorName: e.target.value })}
            placeholder="Optional"
          />
        </label>
        <label className="fin-modal-field">
          <span className="fin-modal-label">{lockedToBill ? 'Remaining' : 'Amount'}</span>
          <input
            className="fin-modal-input"
            type="number"
            min={0}
            step={100}
            value={lockedToBill ? linkedCapexDisplayAmount(line, supplierBills) : (line.amount || '')}
            disabled={lockedToBill}
            onChange={(e) => onPatch({ amount: Number(e.target.value) || 0 })}
          />
        </label>
        {!lockedToBill && (
          <label className="fin-modal-field">
            <span className="fin-modal-label">{line.split === 'lump' ? 'Date' : 'First payment'}</span>
            <input
              className="fin-modal-input"
              type="date"
              value={toInputDate(line.date)}
              onChange={(e) => onPatch({ date: fromInputDate(e.target.value, line.date) })}
            />
          </label>
        )}
      </div>
      {lockedToBill && linkedBill ? (
        <CapexLinkedBillTerms
          bill={linkedBill}
          taskEndDateById={taskEndDateById}
          taskNameById={taskNameById}
        />
      ) : (
        <OutflowSplitFields line={line} onPatch={onPatch} />
      )}
      {allowBillLink && (
        <CapexBillLinkField
          line={line}
          bills={supplierBills}
          billProjectName={billProjectName ?? (() => undefined)}
          onPatch={onPatch}
          canWrite={canWrite}
          taskEndDateById={taskEndDateById}
        />
      )}
      <CapexLineAttachments
        lineId={line.id}
        attachments={line.attachments}
        canWrite={canWrite}
        planId={planId}
        onEnsurePlanId={onEnsurePlanId}
        onChange={(attachments) => onPatch({ attachments })}
        onPreview={onPreviewFile || ((href) => window.open(href, '_blank', 'noopener,noreferrer'))}
      />
    </div>
  );
}

function OutflowRow({
  line,
  onPatch,
  allowBillLink = false,
  supplierBills = [],
  billProjectName,
  canWrite = true,
  taskEndDateById,
  taskNameById,
  planId = null,
  onEnsurePlanId,
  onPreviewFile,
}: {
  line: ScenarioOutflow;
  onPatch: (next: Partial<ScenarioOutflow>) => void;
  allowBillLink?: boolean;
  supplierBills?: SupplierBill[];
  billProjectName?: (bill: SupplierBill) => string | undefined;
  canWrite?: boolean;
  taskEndDateById?: Map<string, Date>;
  taskNameById?: Map<string, string>;
  planId?: string | null;
  onEnsurePlanId?: () => Promise<string | null>;
  onPreviewFile?: (href: string, file: ScenarioOutflowAttachment) => void;
}) {
  const lockedToBill = !!line.linkedBillId?.trim();
  const linkedBill = supplierBills.find((bill) => bill.id === line.linkedBillId?.trim());
  return (
    <div className="fin-planning-line-main">
      <div className="fin-modal-grid">
        <label className="fin-modal-field">
          <span className="fin-modal-label">Label</span>
          <input
            className="fin-modal-input"
            value={line.label}
            onChange={(e) => onPatch({ label: e.target.value })}
          />
        </label>
        <label className="fin-modal-field">
          <span className="fin-modal-label">Vendor</span>
          <input
            className="fin-modal-input"
            value={line.vendorName}
            disabled={lockedToBill}
            onChange={(e) => onPatch({ vendorName: e.target.value })}
            placeholder="Optional"
          />
        </label>
        <label className="fin-modal-field">
          <span className="fin-modal-label">{lockedToBill ? 'Remaining' : 'Amount'}</span>
          <input
            className="fin-modal-input"
            type="number"
            min={0}
            step={100}
            value={lockedToBill ? linkedCapexDisplayAmount(line, supplierBills) : (line.amount || '')}
            disabled={lockedToBill}
            onChange={(e) => onPatch({ amount: Number(e.target.value) || 0 })}
          />
        </label>
        {!lockedToBill && (
          <label className="fin-modal-field">
            <span className="fin-modal-label">{line.split === 'lump' ? 'Date' : 'First payment'}</span>
            <input
              className="fin-modal-input"
              type="date"
              value={toInputDate(line.date)}
              onChange={(e) => onPatch({ date: fromInputDate(e.target.value, line.date) })}
            />
          </label>
        )}
      </div>
      {lockedToBill && linkedBill ? (
        <CapexLinkedBillTerms
          bill={linkedBill}
          taskEndDateById={taskEndDateById}
          taskNameById={taskNameById}
        />
      ) : (
        <OutflowSplitFields line={line} onPatch={onPatch} />
      )}
      {allowBillLink && (
        <CapexBillLinkField
          line={line}
          bills={supplierBills}
          billProjectName={billProjectName ?? (() => undefined)}
          onPatch={onPatch}
          canWrite={canWrite}
          taskEndDateById={taskEndDateById}
        />
      )}
      <CapexLineAttachments
        lineId={line.id}
        attachments={line.attachments}
        canWrite={canWrite}
        planId={planId}
        onEnsurePlanId={onEnsurePlanId}
        onChange={(attachments) => onPatch({ attachments })}
        onPreview={onPreviewFile || ((href) => window.open(href, '_blank', 'noopener,noreferrer'))}
      />
    </div>
  );
}

function ShiftRow({
  line,
  items,
  onPatch,
}: {
  line: ScenarioTimingShift;
  items: CashFlowProjectionItem[];
  onPatch: (next: Partial<ScenarioTimingShift>) => void;
}) {
  const item = items.find((i) => projectionItemKey(i) === line.itemKey);
  return (
    <div className="fin-planning-line-main">
      <label className="fin-modal-toggle">
        <input
          type="checkbox"
          checked={line.enabled}
          onChange={(e) => onPatch({ enabled: e.target.checked })}
        />
        <span>Shift</span>
      </label>
      <strong>{item ? item.projectLabel : 'Live item'}</strong>
      <span className="fin-planning-line-meta">
        {item ? itemSummary(item) : line.itemKey}
      </span>
      <div className="fin-forecast-toggle" role="group" aria-label="Shift days">
        {SHIFT_STEPS.map((days) => (
          <button
            key={days}
            type="button"
            className={line.shiftDays === days ? 'active' : ''}
            onClick={() => onPatch({ shiftDays: days })}
          >
            {days > 0 ? `+${days}d` : `${days}d`}
          </button>
        ))}
      </div>
    </div>
  );
}

export default CashPlanningModal;
